import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import type { FilterOption } from './FilterSelect';
import styles from './ui.module.css';

interface FilterAsyncMultiSelectProps {
  label: string;
  value: FilterOption[];
  onChange: (value: FilterOption[]) => void;
  loadOptions: (input: string) => Promise<FilterOption[]>;
  placeholder?: string;
  limitTags?: number;
  disabled?: boolean;
}

const DEBOUNCE_MS = 300;

/**
 * FilterAsyncSelect's multi-pick sibling — server-searched options, several
 * picked at once, rendered as chips. Replaces react-select's `isMulti`
 * AsyncSelect (ClientMultiFilter, SchemeMultiFilter).
 */
const FilterAsyncMultiSelect: React.FC<FilterAsyncMultiSelectProps> = ({
  label,
  value,
  onChange,
  loadOptions,
  placeholder,
  limitTags = 2,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      loadOptions(inputValue)
        .then(results => {
          if (requestId.current !== id) return;
          const missing = value.filter(v => !results.some(r => r.value === v.value));
          setOptions([...missing, ...results]);
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inputValue, loadOptions]);

  return (
    <Autocomplete
      multiple
      size="small"
      limitTags={limitTags}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      getOptionLabel={opt => opt.label}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      filterOptions={x => x}
      loading={loading}
      disabled={disabled}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.value}
            label={option.label}
            size="small"
            className={styles.filterChip}
          />
        ))
      }
      renderInput={params => (
        // shrink: without it, the unfocused label sits over the placeholder's slot.
        <TextField
          {...params}
          label={label}
          placeholder={placeholder ?? `All ${label}`}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}
    />
  );
};

export default FilterAsyncMultiSelect;
